import React, { useState } from "react";
import { X, EnvelopeSimple, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { subscribeNewsletter } from "@/lib/api";

/**
 * "Stay in the loop on VA news" — single-opt-in newsletter capture modal.
 *
 * Single field (email), single click. No double-opt-in. The submitted address
 * lands in `db.newsletter_subscribers` and the admin can export a CSV from
 * Admin → Newsletter at any time to mail-merge from outside the app.
 */
export default function NewsletterModal({ open, onClose, source = "sidebar-pin" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [doneMsg, setDoneMsg] = useState("");

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const trimmed = email.trim();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) {
      setErr("That doesn't look like a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const res = await subscribeNewsletter(trimmed, source);
      setDoneMsg(
        res?.already
          ? "You're already on the list — we'll keep you in the loop."
          : "You're in. Welcome aboard."
      );
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't subscribe right now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    setEmail(""); setErr(""); setDoneMsg(""); setBusy(false);
    onClose?.();
  }

  return (
    <div className="modal-backdrop" onClick={handleClose} data-testid="newsletter-overlay">
      <div
        className="modal-panel newsletter-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="newsletter-modal"
      >
        <div className="modal-header">
          <div className="newsletter-icon" aria-hidden="true">
            <EnvelopeSimple size={20} weight="duotone" />
          </div>
          <div>
            <div className="modal-title">VA news & app updates</div>
            <div className="modal-subtitle">
              {doneMsg ? "Thanks!" : "Just your email. We send important VA rule changes, claim tips, and app updates — never spam."}
            </div>
          </div>
          <button className="icon-btn" onClick={handleClose} aria-label="Close" data-testid="newsletter-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="modal-body newsletter-body">
          {doneMsg ? (
            <div className="newsletter-success" data-testid="newsletter-success">
              <CheckCircle size={42} weight="duotone" />
              <p>{doneMsg}</p>
              <button
                type="button"
                className="btn btn--accent"
                onClick={handleClose}
                data-testid="newsletter-done"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="newsletter-form">
              <label className="field">
                <span className="field-label"><EnvelopeSimple size={12} weight="bold" /> EMAIL</span>
                <input
                  type="email"
                  className="field-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  required
                  data-testid="newsletter-email"
                />
              </label>

              {err && <div className="auth-prompt-error" data-testid="newsletter-error">{err}</div>}

              <button
                type="submit"
                className="btn btn--accent newsletter-submit"
                disabled={busy}
                data-testid="newsletter-submit"
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {busy ? "Subscribing..." : "Subscribe"}
              </button>

              <p className="newsletter-fineprint">
                Unsubscribe anytime — every email has a one-click opt-out link.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
